import { format } from 'date-fns'
import * as Sentry from '@sentry/react-native'
import { rootDb, fieldValue } from './firebaseFirestore'
import { sanitizeForFirestore } from './productSanitize'
import { dualWriteUpdate } from './updates'
import { dateRangeWithTimezoneAndAutoclose, businessDayAsLocalDate, getLocalStoreDate, getMonthlyStatDocID, dailyStatIDForRecord, normalizeDate, type DateLike } from '../../utils/timezoneFunctions'
import { getDefaultStore } from 'jotai'
import { dbAtom, userIdAtom, organizationIdAtom, storesDictAtom, usersDictAtom, rolesDictAtom } from '../AtomsProvider'
import { getStatsForDays } from './firebase'
import { validateDailyStats } from '../../utils/validationFunctions'
import { dbWriteLogger } from '../../utils/databaseWriteLogger'
import { workingHoursApi, transactionsApi } from '../API/typedApiClient'
import { bestEffortDualWrite, bestEffortDualWriteDelete } from './dualWrite'
import { getFirestoreRef, commitBatchTolerant } from './firestoreRefUtils'
import { assertWWriterAvailable } from '../Write/writeW'
import { workingHoursMirrorPayload } from './workingHoursDualWritePayload'
import { commitGuardedCreate } from './writeClaims'

// ============================================================================
// Types
// ============================================================================

/**
 * Hour value object with numeric value and optional text
 */
interface HourValue {
  value: number
  text?: string
}

/**
 * Role details for salary and bonus calculations
 */
interface RoleDetails {
  salary?: number
  salaryType?: 'hour' | 'day' | 'month'
  roleBonusType?: 'individualSalesPercent' | string
  individualSalesPercent?: number
  minIndividualSales?: number
}

/**
 * Working hours record
 */
export interface WorkingHoursInfo {
  id?: string
  ref?: FirestoreRef
  employeeID?: string
  role?: string
  roleDetails?: RoleDetails
  salaryType?: 'hour' | 'day' | 'month'
  startHour?: HourValue
  endHour?: HourValue
  amount: number
  date: Date | FirestoreTimestamp
  startDate?: Date | FirestoreTimestamp
  endDate?: Date | FirestoreTimestamp
  dailyStatID?: string
  monthlyStatID?: string
  store: string
  employeeBonusAmount?: number
  organizationId?: string
  createdDate?: Date
  updatedDate?: Date
  createdBy?: string
  updatedBy?: string
}

/**
 * Transaction info for salary payments
 */
interface TransactionInfo {
  id?: string
  ref?: FirestoreRef
  value: number
  date: Date | FirestoreTimestamp
  store: string
  sourceWallet?: { id: string } | string
  externalDestination: { id: string; name?: string }
  monthlyStatID?: string
  organizationId?: string
  createdDate?: Date
  updatedDate?: Date
  createdBy?: string
  updatedBy?: string
  /**
   * ATL-1240 — the Firebase UID of the user who made the payment.
   *
   * `PaymentTransactionDetailScreen.js` sets it (`paidBy: context.user.uid`) and the Firestore write
   * here stores it, but this interface never declared it — which is part of why the Supabase mirror
   * could omit it for months without the compiler noticing.
   */
  paidBy?: string
  /**
   * ATL-1240 — the period this salary payment settles. The salary screens compute what is still
   * owed from it, so losing it at the read cutover is not cosmetic.
   */
  paymentRange?: { startDate?: Date | FirestoreTimestamp | string; endDate?: Date | FirestoreTimestamp | string }
  /** ATL-1781 — the payment note (`PaymentTransactionDetailScreen.js` sets it; the write stores it). */
  details?: string
}

/**
 * Date range for queries
 */
export interface DateRange {
  startDate: Date
  endDate: Date
}

/**
 * Daily/Monthly stat fields
 */
interface StatFields {
  salaryAmount?: unknown
  workingHoursQuantity?: unknown
  workingDaysQuantity?: unknown
  monthlySalaryAmount?: unknown
  monthlyWorkingHoursQuantity?: unknown
  monthlyWorkingDaysQuantity?: unknown
  monthlyWorkingMonthsQuantity?: unknown
  [key: string]: unknown
}

/**
 * Stats object keyed by date string
 */
interface Stats {
  [dateKey: string]: {
    id?: string
    date: Date | FirestoreTimestamp
    store?: string
    [key: string]: unknown
  }
}

// Firestore types (simplified since we import from JS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
type FirestoreDb = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FirestoreRef = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FirestoreBatch = any

interface FirestoreTimestamp {
  toDate: () => Date
}

// ============================================================================
// Module State
// ============================================================================

const jotaiStore = getDefaultStore()

// ============================================================================
// Stat Increment Functions
// ============================================================================

/**
 * Generates the fields to increment or decrement in the dailyStats collection based on the working hours record.
 * Used for both saving and deleting working hours.
 *
 * @param info - The new or current working hours info object.
 * @param oldValue - The previous working hours info object, if updating or deleting.
 * @param forFirebase - If true, returns values as fieldValue.increment; otherwise, returns raw numbers.
 * @param negative - If true, inverts the increments (for deletions).
 * @returns An object with the fields to update in dailyStats.
 */
export const dailyStatIncrementsForWorkingHours = (
  info: WorkingHoursInfo,
  oldValue: WorkingHoursInfo | null = null,
  forFirebase = true,
  negative = false
): StatFields => {
  const statFields: StatFields = {}
  if (info.salaryType === 'hour' || (!info.salaryType && oldValue)) {
    const hours = (info.endHour?.value || 0) - (info.startHour?.value || 0)
    const oldHours = oldValue ? ((oldValue.endHour?.value || 0) - (oldValue.startHour?.value || 0)) : 0
    let amount = info.amount - (oldValue?.amount || 0)
    let hoursDiff = hours - oldHours
    if (negative) {
      amount = -amount
      hoursDiff = -hoursDiff
    }
    statFields.salaryAmount = forFirebase ? fieldValue.increment(amount) : amount
    statFields.workingHoursQuantity = forFirebase ? fieldValue.increment(hoursDiff) : hoursDiff
  } else if (info.salaryType === 'day') {
    let amount = info.amount
    let days = 1
    if (negative) {
      amount = -amount
      days = -days
    }
    statFields.salaryAmount = forFirebase ? fieldValue.increment(amount) : amount
    statFields.workingDaysQuantity = forFirebase ? fieldValue.increment(days) : days
  }
  return statFields
}

/**
 * Returns the fields to increment or set in monthlyStats for a working hours record.
 * Similar to dailyStatIncrementsForWorkingHours, but for monthly aggregation.
 * Also adds stats separately by employee (e.g., employeeID + 'MonthlySalaryAmount').
 *
 * @param info - The new or current working hours info object.
 * @param oldValue - The previous working hours info object, if updating or deleting.
 * @param forFirebase - If true, returns values as fieldValue.increment; otherwise, returns raw numbers.
 * @param negative - If true, inverts the increments (for deletions).
 * @returns An object with the fields to update in monthlyStats, including per-employee fields.
 */
export const monthlyStatIncrementsForWorkingHours = (
  info: WorkingHoursInfo,
  oldValue: WorkingHoursInfo | null = null,
  forFirebase = true,
  negative = false
): StatFields => {
  const statFields: StatFields = {}
  const emp = info.employeeID
  if (info.salaryType === 'hour' || (!info.salaryType && oldValue)) {
    const hours = (info.endHour?.value || 0) - (info.startHour?.value || 0)
    const oldHours = oldValue ? ((oldValue.endHour?.value || 0) - (oldValue.startHour?.value || 0)) : 0
    let amount = info.amount - (oldValue?.amount || 0)
    let hoursDiff = hours - oldHours
    if (negative) {
      amount = -amount
      hoursDiff = -hoursDiff
    }
    statFields.monthlySalaryAmount = forFirebase ? fieldValue.increment(amount) : amount
    statFields.monthlyWorkingHoursQuantity = forFirebase ? fieldValue.increment(hoursDiff) : hoursDiff
    if (emp) {
      statFields[emp + 'MonthlySalaryAmount'] = forFirebase ? fieldValue.increment(amount) : amount
      statFields[emp + 'MonthlyWorkingHoursQuantity'] = forFirebase ? fieldValue.increment(hoursDiff) : hoursDiff
    }
  } else if (info.salaryType === 'day') {
    let amount = info.amount
    let days = 1
    if (negative) {
      amount = -amount
      days = -days
    }
    statFields.monthlySalaryAmount = forFirebase ? fieldValue.increment(amount) : amount
    statFields.monthlyWorkingDaysQuantity = forFirebase ? fieldValue.increment(days) : days
    if (emp) {
      statFields[emp + 'MonthlySalaryAmount'] = forFirebase ? fieldValue.increment(amount) : amount
      statFields[emp + 'MonthlyWorkingDaysQuantity'] = forFirebase ? fieldValue.increment(days) : days
    }
  } else if (info.salaryType === 'month') {
    let amount = info.amount - (oldValue?.amount || 0)
    let months = 1
    if (negative) {
      amount = -amount
      months = -months
    }
    statFields.monthlySalaryAmount = forFirebase ? fieldValue.increment(amount) : amount
    statFields.monthlyWorkingMonthsQuantity = forFirebase ? fieldValue.increment(months) : months
    if (emp) {
      statFields[emp + 'MonthlySalaryAmount'] = forFirebase ? fieldValue.increment(amount) : amount
      statFields[emp + 'MonthlyWorkingMonthsQuantity'] = forFirebase ? fieldValue.increment(months) : months
    }
  }
  return statFields
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Creates or updates a working hours record in the workingHours collection and updates the corresponding dailyStats document.
 * If oldValue is provided, updates the existing record; otherwise, creates a new one.
 *
 * @param oldValue - The previous working hours record, if updating.
 * @param info - The working hours data to save.
 * @returns A reference to the saved/updated working hours document.
 */
export const saveWorkingHours = async (
  oldValue: WorkingHoursInfo | null,
  info: WorkingHoursInfo
): Promise<FirestoreRef> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#saveWorkingHours')
  const db = jotaiStore.get(dbAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)
  const currentUserUID = jotaiStore.get(userIdAtom)
  const updatedInfo = {
    ...info,
    createdDate: new Date(),
    updatedDate: new Date(),
    createdBy: currentUserUID,
    updatedBy: currentUserUID,
    organizationId: organizationId
  }

  const batch: FirestoreBatch = rootDb.batch()
  // ATL-2061: an EDIT resolves the record through getFirestoreRef, exactly as deleteWorkingHours
  // does. On an organization that reads from Supabase, `oldValue.ref` is the plain `{ id }` shim
  // the read path attaches (SalaryScreen's `ref: { id: item.id }`, the backend transform's
  // `ref: { id: firestoreId }`), and RNFirebase's `batch.set` rejects anything that is not a
  // DocumentReference synchronously — the edit never reached Firestore. A real ref passes through.
  const ref = oldValue ? getFirestoreRef(oldValue, 'workingHours') : db.collection('workingHours').doc()
  batch.set(ref, updatedInfo)

  // update user
  if (info.employeeID) {
    batch.update(rootDb.collection('users').doc(info.employeeID), {
      salaryCreditedAmount: fieldValue.increment(info.amount),
    })
  }
  // update stats
  const statDocName = info.dailyStatID
  const statFields = dailyStatIncrementsForWorkingHours(info, oldValue, true, false)
  if (info.salaryType !== 'month') {
    batch.set(db.collection('dailyStats').doc(statDocName), {
      ...statFields,
      date: info.date,
      store: info.store,
      organizationId: organizationId
    }, {merge: true})
  }
  const infoDate = (info.date as FirestoreTimestamp)?.toDate?.() || info.date as Date
  const monthlyStatDocName = info.monthlyStatID || getMonthlyStatDocID(info.store, infoDate)
  const monthlyStatFields = monthlyStatIncrementsForWorkingHours(info, oldValue, true, false)
  batch.set(db.collection('monthlyStats').doc(monthlyStatDocName), {
    ...monthlyStatFields,
    date: info.date,
    store: info.store,
    organizationId: organizationId
  }, {merge: true})

  // ATL-2004: only a NEW working-hours record can carry a write claim — an edit's record already exists,
  // so its presence could not tell a refused re-delivery from a refusal for another reason.
  await (oldValue ? batch.commit() : commitGuardedCreate('workingHours.create', db, ref, batch))

  // Dual write to Supabase via NestJS backend.
  // ATL-1954: the mirror is built from the same `info` the batch above stored, so the period
  // (`startDate`/`endDate`), the role snapshot and the stat ids reach Supabase too — their absence
  // white-screened the Salary screen of a Supabase-read org. Built inside the dual-write callback, so
  // a malformed value is a dual-write failure, never an exception after Firestore has committed.
  const isUpdate = !!oldValue
  if (isUpdate) {
    // Use firestore-based endpoint since we have the Firestore ID, not Supabase UUID
    await bestEffortDualWrite('workingHours.update', () =>
      workingHoursApi.updateWorkingHoursByFirestoreId(oldValue!.id!, workingHoursMirrorPayload(info)))
  } else {
    await bestEffortDualWrite('workingHours.create', () => {
      const payload = workingHoursMirrorPayload(info)
      return workingHoursApi.createWorkingHours({ ...payload, firestoreId: ref.id, employeeId: payload.employeeId ?? '' })
    })
  }

  return ref
}

/**
 * Deletes a working hours record and updates the corresponding dailyStats document to decrement the relevant fields.
 * Also logs the deletion in the updates collection.
 *
 * @param oldValue - The working hours record to delete.
 * @throws {Error} when called with no record — see below.
 */
export const deleteWorkingHours = async (oldValue: WorkingHoursInfo | null | undefined): Promise<void> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#deleteWorkingHours')
  // ATL-963 / QARASAY-KASSA-265: ShiftScreen's employee modal is shared by its edit and add flows and
  // used to offer Delete in both, so this was reached with `null` and threw "Cannot read
  // property 'ref' of null" — 41 events / 15 users. The button is now gated, but the contract
  // belongs here too: the parameter is explicitly nullable and a missing record is refused by
  // name, before the batch is touched. No working-hours row was ever deleted by the old throw
  // (it fired on the batch's first statement), so payroll data is intact.
  if (!oldValue) {
    throw new Error(
      `deleteWorkingHours: no working hours record to delete (received ${oldValue}). ` +
      'The caller must pass the record being removed.',
    )
  }
  const db = jotaiStore.get(dbAtom)
  const currentUserUID = jotaiStore.get(userIdAtom)
  const batch: FirestoreBatch = rootDb.batch()
  // Resolve through the shared resolver rather than reading `.ref` directly: for an org that
  // reads from Supabase, `oldValue.ref` is a mock ref, and `batch.delete(mockRef)` does not
  // delete the Firestore twin. getFirestoreRef passes real refs through untouched and rebuilds
  // organizations/{org}/workingHours/{id} from a mock ref or a bare id.
  batch.delete(getFirestoreRef(oldValue, 'workingHours'))

  // update user
  if (oldValue.employeeID) {
    batch.update(rootDb.collection('users').doc(oldValue.employeeID), {
      salaryCreditedAmount: fieldValue.increment(-oldValue.amount),
    })
  }
  const oldDate = (oldValue.date as FirestoreTimestamp)?.toDate?.() || oldValue.date as Date
  // ATL-1013: business day, not the device's calendar day — the same shape the
  // monthlyStatID fallback two lines below has always had. See deleteSale.
  const statDocName = dailyStatIDForRecord(oldValue.dailyStatID as string | undefined, oldValue.store as string | undefined, oldValue.date as DateLike)
  const statFields = dailyStatIncrementsForWorkingHours(oldValue, null, true, true)
  if (oldValue.salaryType !== 'month') {
    batch.set(db.collection('dailyStats').doc(statDocName), statFields, {merge: true})
  }
  const monthlyStatDocName = oldValue.monthlyStatID || getMonthlyStatDocID(oldValue.store, oldDate)
  const monthlyStatFields = monthlyStatIncrementsForWorkingHours(oldValue, null, true, true)
  batch.set(db.collection('monthlyStats').doc(monthlyStatDocName), monthlyStatFields, {merge: true})

  // write to updates
  // ATL-911: bind the ref BEFORE building the record, so the document's generated id is
  // part of it. An anonymous `.doc()` mints an id and discards it, which is why the
  // Supabase mirror landed with firestore_id NULL and its Firestore twin read as missing.
  const deleteWorkingHourUpdateRef = db.collection('updates').doc()
  const deleteWorkingHourUpdate = {
    firestoreId: (deleteWorkingHourUpdateRef as { id: string }).id,
    date: new Date(),
    cashierUID: currentUserUID,
    type: 'deleteWorkingHour',
    // ATL-1954: sanitized, as deleteSale and deleteProduct do — not merely stripped of undefined.
    // On an organization that reads from Supabase the record came from the API, so its dates are
    // TimestampLike objects carrying a `toDate` FUNCTION and its `ref` is a mock. Firestore rejects
    // a function anywhere in a document ("Unsupported field value: a function … dataBefore.createdAt
    // .toDate"), the whole batch fails, and the screen swallowed the error: the merchant pressed
    // Delete and the accrual stayed. A monthly accrual cannot be edited, so delete is the only way
    // to correct one. sanitizeForFirestore turns each TimestampLike into a Date and drops `ref`
    // (no reader of `dataBefore` uses it — the Updates screen and the Supabase mirror do not).
    dataBefore: sanitizeForFirestore(oldValue),
  }
  batch.set(deleteWorkingHourUpdateRef, deleteWorkingHourUpdate)
  await batch.commit()

  // Mirror the audit-log entry to Supabase (best-effort)
  await dualWriteUpdate(deleteWorkingHourUpdate)

  // Dual write to Supabase via NestJS backend
  // Use firestore-based endpoint since we have the Firestore ID, not Supabase UUID
  await bestEffortDualWriteDelete('workingHours.delete', () => workingHoursApi.deleteWorkingHoursByFirestoreId(oldValue.id!), { firestoreId: oldValue.id! })
}

// ============================================================================
// Query Functions
// ============================================================================

/**
 * Fetches working hours records within a specified date range, optionally filtered by user and/or store.
 * Can return results as a flat array or grouped by date.
 *
 * @param range - { startDate: Date, endDate: Date }
 * @param userId - (optional) Employee ID to filter by.
 * @param flat - If true, returns a flat array; otherwise, groups by date.
 * @param store - (optional) Store ID to filter by.
 * @returns Array of working hours records, either flat or grouped by date.
 */
export const getWorkingHoursOnDateRange = async (
  range: DateRange,
  userId: string | null = null,
  flat = false,
  store: string | null = null
): Promise<WorkingHoursInfo[] | Array<{ title: string; data: WorkingHoursInfo[] }>> => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  let querySnapshot = db.collection('workingHours')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  if (userId) {
    querySnapshot = querySnapshot.where('employeeID', '==', userId)
  }
  querySnapshot = await querySnapshot.where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const docs: WorkingHoursInfo[] = []
    querySnapshot.forEach(function (doc: FirestoreRef) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      docs.push(snap)
    })
    return docs
  } else {
    const docsGrouped: Record<string, WorkingHoursInfo[]> = {} // date key - array of working hours
    querySnapshot.forEach(function (doc: FirestoreRef) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      const date = format(getLocalStoreDate(snap.store, snap.date.toDate()), 'd/M/y EEEE')
      const newArr = docsGrouped[date] ? [...docsGrouped[date]] : []
      newArr.push(snap)
      docsGrouped[date] = [...newArr]
    })
    const docsArr: Array<{ title: string; data: WorkingHoursInfo[] }> = []
    for (const key in docsGrouped) {
      const newData = {
        title: key,
        data: docsGrouped[key]
      }
      docsArr.push(newData)
    }
    return docsArr
  }
}

/**
 * Fetches working hours records with salaryType: 'month' that overlap with the given date range, optionally filtered by store.
 *
 * @param range - { startDate: Date, endDate: Date }
 * @param store - (optional) Store ID to filter by.
 * @returns Array of working hours records with salaryType: 'month'.
 */
export const getMonthlyWorkingHoursOnDateRange = async (
  range: DateRange,
  store: string | null = null
): Promise<WorkingHoursInfo[]> => {
  const db = jotaiStore.get(dbAtom)
  // get wh with start date between the range
  let querySnapshot = db.collection('workingHours').where('salaryType', '==', 'month')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  querySnapshot = await querySnapshot.where('startDate', '>=', range.startDate)
    .where('startDate', '<=', range.endDate)
    .get()
  const docs: WorkingHoursInfo[] = []
  querySnapshot.forEach(function (doc: FirestoreRef) {
    const snap = doc.data()
    snap.id = doc.id
    snap.ref = doc.ref
    docs.push(snap)
  })
  const startOfMonth = new Date(range.startDate)
  startOfMonth.setDate(1)
  startOfMonth.setHours(0,0,0,0)
  // get wh with start date before the range and end date after the range, but this is not possible so after the start of month
  let start2QuerySnapshot = db.collection('workingHours').where('salaryType', '==', 'month')
  if (store) {
    start2QuerySnapshot = start2QuerySnapshot.where('store', '==', store)
  }
  start2QuerySnapshot = await start2QuerySnapshot.where('startDate', '<=', range.startDate)
    .where('startDate', '>=', startOfMonth)
    .get()
  start2QuerySnapshot.forEach(function (doc: FirestoreRef) {
    const snap = doc.data()
    snap.id = doc.id
    snap.ref = doc.ref
    if (snap.id !== docs.find(d => d.id === snap.id)?.id) {
      docs.push(snap)
    }
  })
  return docs
}

// ============================================================================
// Transaction Functions
// ============================================================================

/**
 * Saves a new working hour payment transaction and updates the corresponding monthlyStats document to increment the relevant fields.
 * Also updates the user's salaryPaidAmount field.
 *
 * @param info - The transaction information.
 * @returns A reference to the saved transaction document.
 */
/**
 * A Firestore Timestamp, a `Date` or an already-ISO string, as an ISO instant.
 *
 * Every value that reaches the Supabase mirror from this file goes through here, because the three
 * shapes are indistinguishable at the call site and only one of them survives `@IsString()`
 * validation on the backend.
 */
const toIsoInstant = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  const withToDate = value as FirestoreTimestamp
  if (typeof withToDate?.toDate === 'function') return withToDate.toDate().toISOString()
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  if (typeof value === 'string') return value
  return undefined
}

/**
 * ATL-1240 — a `{startDate,endDate}` range with both bounds as ISO strings, or `undefined`.
 *
 * Returns `undefined` when NEITHER bound can be read, so a range this function could not parse at
 * all is OMITTED rather than sent as an object the backend will reject — a rejected transaction is a
 * permanently lost row (ATL-782), an omitted optional field is not.
 *
 * Where exactly ONE bound parses it returns that bound alone. Both are optional on
 * `PaymentRangeDto`, and half the period is strictly more than the `null` the column holds today;
 * the alternative — dropping a readable bound because its partner was not — would discard
 * information to no end.
 */
const toIsoPaymentRange = (
  range: unknown,
): { startDate?: string; endDate?: string } | undefined => {
  if (range === null || typeof range !== 'object') return undefined
  const r = range as { startDate?: unknown; endDate?: unknown }
  const startDate = toIsoInstant(r.startDate)
  const endDate = toIsoInstant(r.endDate)
  if (startDate === undefined && endDate === undefined) return undefined
  return {
    ...(startDate !== undefined ? { startDate } : {}),
    ...(endDate !== undefined ? { endDate } : {}),
  }
}

export const saveNewWorkingHourPaymentTransaction = async (
  info: TransactionInfo
): Promise<FirestoreRef> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#saveNewWorkingHourPaymentTransaction')
  const db = jotaiStore.get(dbAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)
  const currentUserUID = jotaiStore.get(userIdAtom)
  const batch: FirestoreBatch = rootDb.batch()
  const newRef = db.collection('transactions').doc()
  const updatedInfo = {
    ...info,
    createdDate: new Date(),
    updatedDate: new Date(),
    createdBy: currentUserUID,
    updatedBy: currentUserUID,
    organizationId: organizationId,
  }
  batch.set(newRef, updatedInfo)

  // update monthlyStats
  const infoDate = (info.date as FirestoreTimestamp)?.toDate?.() || info.date as Date
  const monthlyStatDocName = info.monthlyStatID || getMonthlyStatDocID(info.store, infoDate)
  batch.set(db.collection('monthlyStats').doc(monthlyStatDocName), {
    salaryPaid: fieldValue.increment(info.value),
    [info.externalDestination.id + 'SalaryPaid']: fieldValue.increment(info.value),
    date: info.date,
    store: info.store,
    organizationId: info.organizationId
  }, {merge: true})

  // update user
  batch.update(rootDb.collection('users').doc(info.externalDestination.id), {
    salaryPaidAmount: fieldValue.increment(info.value),
  })
  await commitGuardedCreate('workingHours.payment.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  // Note: Working hour payment transactions are handled via /transactions endpoint
  const sourceWalletId = typeof info.sourceWallet === 'string'
    ? info.sourceWallet
    : info.sourceWallet?.id
  // ⛔ FALL BACK TO THE RAW VALUE, do not omit. `toIsoInstant` returns `undefined` for anything it
  // cannot read, and an omitted `date` makes the backend substitute `new Date().toISOString()` — so
  // an unreadable date would silently file a MONEY ROW under today. The expression this replaced
  // forwarded the raw value, which the DTO rejects with a 400. A loud refusal on a money row beats a
  // quiet wrong date, so the normalisation is a best effort and never a filter.
  const dateIso = toIsoInstant(info.date) ?? (info.date as unknown as string)
  // ATL-1240 — `paidBy` and `paymentRange` were absent from this literal while the Firestore write
  // above stores the WHOLE `info`, so every salary payment written by a live till landed in Supabase
  // with both columns NULL. Measured on prod 2026-09-07: 417 rows across 7 organizations, and the
  // SAME 417 on both columns (0 rows null on only one of them) — the signature of one payload
  // omission rather than two independent defects. The generic sibling `saveTransaction` sends both,
  // the DTO accepts both and `insert_transaction` persists both; nothing downstream was missing.
  //
  // ⚠️ The range bounds are normalised to ISO strings rather than forwarded raw. `PaymentRangeDto`
  // validates them with `@IsString()`, a Firestore Timestamp serialises to `{_seconds,_nanoseconds}`,
  // and a 400 on this endpoint is classified client-side as a DETERMINISTIC dual-write failure that
  // is never retried (ATL-782). Forwarding the raw value would convert a silently-absent field into
  // a permanently lost transaction — a strictly worse defect than the one being fixed.
  const paymentRangeIso = toIsoPaymentRange(info.paymentRange)
  await bestEffortDualWrite('workingHours.createPaymentTransaction', () => transactionsApi.createTransaction({
    firestoreId: newRef.id,
    organizationId: info.organizationId || '',
    storeId: info.store,
    type: 'expense',
    value: info.value,
    sourceWallet: sourceWalletId,
    linkedDocType: 'workingHour',
    externalDestination: info.externalDestination,
    date: dateIso,
    // Omitted rather than sent as null when the payment carries neither: an absent optional field
    // and an explicit null are not the same write.
    ...(info.paidBy ? { paidBy: info.paidBy } : {}),
    ...(paymentRangeIso ? { paymentRange: paymentRangeIso } : {}),
    // ATL-1781 — the payment note, which the Firestore write above stores as part of `info`.
    // Omitted when absent (the same rule as the two fields above); an empty note is sent as is.
    ...(typeof info.details === 'string' ? { details: info.details } : {}),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any))

  return newRef
}

/**
 * ATL-2061 — the document and keys a salary-payment reversal writes to, read from either read
 * shape (Firestore snapshot or the backend's `TransactionFirestoreResponse`), or a refusal.
 *
 * The reversal must land on EXACTLY the document and keys `saveNewWorkingHourPaymentTransaction`
 * incremented, because `batch.set(..., {merge: true})` on a wrong id does not fail — it creates a
 * new monthlyStats document and the delete "succeeds" while the real totals keep the payment
 * forever. That is worse than the original error, so every input the key is built from is checked
 * here, before the batch is touched, and anything unreadable is refused by name:
 *
 *   - `date`: a Firestore Timestamp, a Date, the API client's TimestampLike (`deserializeDates`),
 *     or an ISO string the client could not wrap. `normalizeDate` decodes all four to the same
 *     instant the create path passed to `getMonthlyStatDocID`. An Invalid Date is refused — it
 *     would format to a garbage id, and `getMonthlyStatDocID`'s default `new Date()` would file
 *     the reversal under TODAY's month.
 *   - `store`: the Firestore store id on both shapes (the backend resolves the UUID through its
 *     stores dict; measured 2026-11-03: 0 of 2,186 live workingHour transactions without a store
 *     firestore id). It must be a non-empty string — `getMonthlyStatDocID(null, …)` silently uses
 *     the CURRENT USER's store.
 *   - `monthlyStatID`: the Supabase shape does not carry it, and no live creator sets it, so the
 *     id is derived from (store, date) exactly as the create path derives it. A stored one wins.
 *   - `externalDestination.id`: the employee's Firebase uid on both shapes (measured: 0 of 2,186
 *     rows hold a UUID). It names `<uid>SalaryPaid` and `users/<uid>`, so it must be a string.
 *   - `value`: a finite number (`z.number()` on the API shape) — `increment(NaN)` would poison
 *     three running totals.
 */
export const workingHourPaymentReversalTarget = (
  info: TransactionInfo,
): { monthlyStatDocName: string; employeeId: string; value: number } => {
  const refusal = (what: string): Error =>
    new Error(`deleteWorkingHourPaymentTransaction: refusing to reverse salary payment ${info?.id ?? '(no id)'}: ${what}`)
  if (!info) throw refusal('no transaction')
  const value: unknown = info.value
  if (typeof value !== 'number' || !Number.isFinite(value)) throw refusal(`value is not a finite number (${String(value)})`)
  const employeeId: unknown = info.externalDestination?.id
  if (typeof employeeId !== 'string' || employeeId === '') throw refusal('externalDestination.id is not an employee id')
  const storedMonthlyStatID: unknown = info.monthlyStatID
  if (typeof storedMonthlyStatID === 'string' && storedMonthlyStatID !== '') {
    return { monthlyStatDocName: storedMonthlyStatID, employeeId, value }
  }
  const store: unknown = info.store
  if (typeof store !== 'string' || store === '') throw refusal('store is not a store id')
  const infoDate = normalizeDate(info.date)
  if (!infoDate || Number.isNaN(infoDate.getTime())) throw refusal(`date is unreadable (${String(info.date)})`)
  // `getMonthlyStatDocID` is declared `string | null` (timezoneFunctions.d.ts); a null id names no
  // document, so it is refused rather than written.
  const monthlyStatDocName = getMonthlyStatDocID(store, infoDate)
  if (typeof monthlyStatDocName !== 'string' || monthlyStatDocName === '') throw refusal('no monthlyStats id for this store and date')
  return { monthlyStatDocName, employeeId, value }
}

/**
 * Deletes a working hour payment transaction and updates the corresponding monthlyStats document to decrement the relevant fields.
 *
 * @param ref - The reference to the transaction document: a DocumentReference (Firestore read) or
 *              the `{ id }` shim the Supabase read shape carries — resolved through getFirestoreRef.
 * @param info - The transaction information, in either read shape.
 */
export const deleteWorkingHourPaymentTransaction = async (
  ref: FirestoreRef,
  info: TransactionInfo
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#deleteWorkingHourPaymentTransaction')
  const db = jotaiStore.get(dbAtom)
  // ATL-2061: resolve every key before the batch exists, so a refusal writes nothing.
  const { monthlyStatDocName, employeeId, value } = workingHourPaymentReversalTarget(info)
  // ATL-2061 (ATL-1358 class): on a Supabase-read org `ref` is the backend transform's plain
  // `{ id }` shim, which RNFirebase's `batch.delete` rejects synchronously. Resolve it to
  // organizations/{org}/transactions/{id}, as deleteTransaction's other branch always has.
  const transactionRef = getFirestoreRef(ref, 'transactions')
  const batch: FirestoreBatch = rootDb.batch()
  batch.delete(transactionRef)

  // update monthlyStats — the same document and keys saveNewWorkingHourPaymentTransaction incremented
  batch.set(db.collection('monthlyStats').doc(monthlyStatDocName), {
    salaryPaid: fieldValue.increment(-value),
    [employeeId + 'SalaryPaid']: fieldValue.increment(-value),
  }, {merge: true})

  // update user
  batch.update(rootDb.collection('users').doc(employeeId), {
    salaryPaidAmount: fieldValue.increment(-value),
  })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  const firestoreId: string = transactionRef.id
  await bestEffortDualWriteDelete('workingHours.deletePaymentTransaction', () => transactionsApi.deleteTransaction(firestoreId), { firestoreId })
}

/**
 * Recalculates and updates the user's salaryCreditedAmount and salaryPaidAmount fields
 * by summing all working hours and all salary payment transactions for all time.
 * @param employeeId - The user's UID.
 */
export const recalculateUserSalaryAmounts = async (employeeId: string): Promise<void> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#recalculateUserSalaryAmounts')
  const db = jotaiStore.get(dbAtom)

  // 1. Sum all working hours (salary credited)
  const whSnapshot = await db.collection('workingHours').where('employeeID', '==', employeeId).get()
  let salaryCreditedAmount = 0
  whSnapshot.forEach((doc: FirestoreRef) => {
    const data = doc.data()
    salaryCreditedAmount += (data.amount || 0) + (data.employeeBonusAmount || 0)
  })

  // 2. Sum all salary payment transactions (salary paid)
  const txSnapshot = await db.collection('transactions').where('externalDestination.id', '==', employeeId).get()
  let salaryPaidAmount = 0
  txSnapshot.forEach((doc: FirestoreRef) => {
    const data = doc.data()
    salaryPaidAmount += data.value || 0
  })

  // 3. Update user document
  await rootDb.collection('users').doc(employeeId).update({
    salaryCreditedAmount,
    salaryPaidAmount,
  })
}

// ============================================================================
// Bonus Calculation Functions
// ============================================================================

/**
 * Counts employee bonuses for a given date range, store, and user.
 *
 * @param whs - Array of working hour records.
 * @param range - Date range object.
 * @param store - Store ID.
 * @param userId - User ID.
 * @param stats - Stats object.
 * @param log - Whether to log the process.
 * @param screenName - Screen name for logging.
 */
/**
 * ATL-1132: mirror an `employeeBonusAmount` change into Supabase.
 *
 * Every other writer in this module dual-writes (`saveWorkingHours` :329/:340,
 * `deleteWorkingHours` :423, the payment-transaction pair :594/:640); the five bonus commit
 * blocks below did not. That is not cosmetic: in Supabase read mode `SalaryScreen` re-reads
 * working hours from `workingHoursApi`, so a Firestore-only write persists the bonus to the store
 * nobody is reading and the screen keeps rendering the old value — 0 ₸ on a day the employee
 * earned one. Observed on staging.
 *
 * `updateWorkingHoursByFirestoreId` takes the FIRESTORE id, which is what `wh.id` holds on both
 * read paths (`transformWorkingHours` emits `id: firestore_id || id`).
 *
 * Best-effort by the same rule as its siblings: Firestore has already committed, so a Supabase
 * failure must not throw away a correct Firestore write. `bestEffortDualWrite` reports it.
 */
const dualWriteBonusAmounts = async (
  changes: Array<{ id?: string; bonus: number }>
): Promise<void> => {
  for (const change of changes) {
    if (!change.id) continue
    await bestEffortDualWrite('workingHours.bonus', () =>
      workingHoursApi.updateWorkingHoursByFirestoreId(change.id as string, {
        // `employeeBonusAmount`, not `bonus` — the spelling the create DTO, the response and the
        // Firestore document all use. The backend now accepts both (it read only `bonus` while
        // the client schema declared only `employeeBonusAmount`, and the controller's
        // `Record<string, unknown>` body validated neither, so the value was silently dropped).
        employeeBonusAmount: change.bonus,
      })
    )
  }
}

export const countEmployeeBonuses = async (
  whs: WorkingHoursInfo[] | null = null,
  range: DateRange,
  store: string | null = null,
  userId: string | null = null,
  stats: Stats | null = null,
  log = false,
  screenName: string | null = null,
  /**
   * ATL-1112: the store set `range` was ENCODED with, used only to decode its bounds.
   * Deliberately separate from `store` above, which selects which working hours are processed
   * and may be absent or recovered from the first row — see the stats read below.
   */
  rangeStoreId: string | null = null
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/workingHours.ts#countEmployeeBonuses')
  log = true

  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)
  const storesDict = jotaiStore.get(storesDictAtom)

  // Initialize logger if not already initialized
  if (!dbWriteLogger.db || !dbWriteLogger.organizationId) {
    dbWriteLogger.initialize(db, organizationId as string)
  }

  // Start database write logging with call stack info
  const logId = dbWriteLogger.startFunction('countEmployeeBonuses', {
    store,
    userId,
    employeeId: null, // Will be set per operation
    screen: screenName || null, // Screen name passed from caller, or extracted from call stack
    dateRange: range ? {
      startDate: range.startDate?.toISOString?.() || range.startDate,
      endDate: range.endDate?.toISOString?.() || range.endDate
    } : null
  })

  if (log) console.log('Starting countEmployeeBonuses function')

  // ATL-1153 — track (business day, STORE) pairs, not bare dates.
  //
  // The document reconciled at the end of this function is `dailyStats/<d-M-y>-<store>`, so a date
  // alone does not name it. Two things went wrong while this was a set of dates: on the
  // `MainStats.js` path (`store = null`, so the fetch spans the whole organization) the stores had
  // to be re-derived from the date's records, which reconciled every store that traded that day
  // rather than the ones whose bonuses actually changed; and the monthly branch registered only
  // `firstWh`'s date while clearing bonuses on records spanning the whole month, so every other
  // affected day kept a stale `dailyStats.employeeBonusAmount` forever — this function is its only
  // writer.
  //
  // Recording the pair at the moment of the write also means the reconciliation no longer has to
  // reconstruct which records a date covered.
  const updatedDayStores = new Map<string, { store: string; date: Date }>()

  /**
   * Register the (business day, store) this record's bonus write lands on.
   *
   * `fallbackStore` is the `store` ARGUMENT. A record's own `store` is legitimately null on the
   * Supabase read path — `transformWorkingHours` emits `store: null` when the store UUID cannot be
   * mapped to a Firestore id, and warns about it — and dropping such a record would silently skip
   * the reconciliation while still having rewritten the bonus. The argument is what the call used
   * before, and `SalaryScreen` passes `user.store.id`.
   */
  const trackUpdatedDayStore = (wh: WorkingHoursInfo, fallbackStore: string | null): void => {
    const whStore = wh.store || fallbackStore
    if (!whStore) {
      // Neither the record nor the caller names a store, so no document can be named. Reported
      // rather than dropped: a silent skip here is indistinguishable from "nothing to do".
      Sentry.captureMessage(
        'countEmployeeBonuses: a bonus was written for a record with no resolvable store, so its daily stats cannot be reconciled (ATL-1153)',
        {
          level: 'warning',
          // ATL-2035: daily stats that cannot be reconciled are a books defect — kept in Sentry.
          tags: { books_write_error: 'count-employee-bonuses-unresolvable-store' },
          fingerprint: ['count-employee-bonuses-unresolvable-store', (organizationId as string) || 'unknown'],
          contexts: { working_hours_scope: { workingHourId: wh.id ?? null, employeeID: wh.employeeID ?? null } },
        }
      )
      return
    }
    const whDate = (wh.date as FirestoreTimestamp)?.toDate?.() || wh.date as Date
    // Keyed on the store-local CALENDAR day, so two shifts on the same day dedupe.
    //
    // ⚠️ KNOWN GAP (ATL-1184), pre-existing and unchanged here: for the 17 of 685 live stores
    // with a non-zero `autoCloseShiftTime` this is the autoclose-NAIVE day, while the bonus was
    // written into the autoclose-AWARE one (`saveWorkingHours` writes `dailyStats/<wh.dailyStatID>`,
    // and `dailyStatIDForRecord` falls back to the aware `getDailyStatID`). A shift timestamped
    // before the cutoff therefore has its bonus in day D−1's document while this reconciles day D.
    //
    // Not fixed here on purpose. `validateDailyStats` localizes the instant it is given and derives
    // BOTH the document id and the comparison window from that naive day — self-consistently — and
    // the ATL-1101 note at that resolution warns in terms that an earlier attempt got exactly
    // backwards: the two must move together or a recalculate overwrites the previous day's books.
    // That is a coordinated change to another ticket's core day-resolution, not a call-site tweak.
    const dateKey = format(getLocalStoreDate(whStore, whDate), 'd/M/y')
    // The raw instant is stored: `validateDailyStats` localizes its `date` argument itself, and
    // localizing here as well applied the shift twice (`localDateInStore` is not idempotent).
    updatedDayStores.set(`${dateKey}\u0000${whStore}`, { store: whStore, date: whDate })
  }

  // If store is not specified, use the store from the first working hour record
  let workingHours = whs
  if (!workingHours || workingHours.length === 0) {
    if (log) console.log('No working hours provided, fetching from database')
    workingHours = await getWorkingHoursOnDateRange(range, userId, true, store) as WorkingHoursInfo[]
    if (log) console.log(`Fetched ${workingHours.length} working hour records`)
  }
  if (!store && workingHours.length > 0) {
    store = workingHours[0].store
    if (log) console.log(`Using store from first working hour: ${store}`)
  }
  let statsData: Stats | null = stats
  if (!statsData) {
    if (log) console.log('No stats provided, fetching from database')
    // ATL-1112: `getStatsForDays` derives its `YYYY-MM-DD` bounds by INVERTING the encoding
    // `dateRangeWithTimezoneAndAutoclose` applied, so it must be handed the store set that
    // encoding used. `store` is not reliably that set — it may be null, or recovered from
    // `workingHours[0].store` a few lines up — and decoding a single-store range across all
    // stores hands back a two-day window, on a path that writes employee bonuses.
    statsData = await getStatsForDays(db, range, rangeStoreId || store || '', true) as Stats
    if (log) console.log(`Fetched stats for store: ${store}`)
  }

  if (log) console.log(`Processing ${workingHours.length} working hour records`)

  // Group working hours by employee and date to avoid duplicate bonuses
  const whByEmployeeAndDate: Record<string, WorkingHoursInfo[]> = {}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Prepared for future use
  const _processedEmployeeDates = new Set<string>()

  // ATL-1131: a record with no `employeeID` cannot produce a bonus, and must not be allowed to
  // produce a ZERO one. Every lookup below is built by concatenation —
  // `stat[employeeId + 'SalesAmount']` (:809) — so an absent id yields the key
  // `undefinedSalesAmount`, `netSalesAmount` 0, `calculatedBonus` 0, and this function then WRITES
  // that over the stored bonus and posts an increment to `users/undefined`. Nothing errors; an
  // employee is simply paid nothing.
  //
  // That was live on the Supabase read path: `transformWorkingHours` emitted `employeeId` (a
  // Supabase UUID) and no `employeeID` at all, and `SalaryScreen` hands those records here
  // unchanged. The backend now supplies `employeeID`; this guard is what keeps the failure LOUD
  // rather than a plausible zero if it ever goes missing again — a different read path, a partial
  // response, a rename.
  //
  // Filtered ONCE, here, rather than guarded per loop: `monthlyWhs` below is derived from this
  // same array, and a guard on only the daily/hourly loop would leave the monthly one grouping
  // under the literal key `undefined-<month>` — computing 0 and writing it, which is the exact
  // behaviour this exists to prevent. Doing it once also makes the report below TRUE for every
  // record it counts.
  const identifiedWorkingHours = workingHours.filter((wh) => !!wh.employeeID)
  const unidentified = workingHours.length - identifiedWorkingHours.length
  for (const wh of identifiedWorkingHours) {
    const employeeId = wh.employeeID
    const whDate = (wh.date as FirestoreTimestamp)?.toDate?.() || wh.date as Date
    const dateStr = format(getLocalStoreDate(wh.store, whDate), 'd/M/y')
    // U+0000, not '-': `employeeID` comes from `users.firestore_id`, a free-form text column,
    // and prod already holds one dashed id. `split('-')` would truncate it to its first segment,
    // miss the stat key, compute 0 and then write to `users/<truncated>`.
    const key = `${employeeId}\u0000${dateStr}`

    if (!whByEmployeeAndDate[key]) {
      whByEmployeeAndDate[key] = []
    }
    whByEmployeeAndDate[key].push(wh)
  }

  if (unidentified > 0) {
    const message =
      `countEmployeeBonuses: ${unidentified} of ${workingHours.length} working-hour record(s) ` +
      `carry no employeeID, so no bonus can be attributed to them. They were SKIPPED rather than ` +
      `paid 0 (ATL-1131).`
    console.warn(message)
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { atl1131_working_hours_missing_employee_id: true },
      extra: { unidentified, total: workingHours.length, store },
    })
  }

  if (log) console.log(`Grouped working hours into ${Object.keys(whByEmployeeAndDate).length} employee-date combinations`)

  // Reindex stats by 'd/M/y' for reliable lookups across platforms
  const statsByDay: Record<string, Stats[string]> = {}
  for (const stat of Object.values(statsData || {})) {
    const statDate = (stat?.date as FirestoreTimestamp)?.toDate ? (stat.date as FirestoreTimestamp).toDate() : stat?.date as Date
    // ATL-1116: a Supabase daily-stat row NAMES its business day in `businessDate`; running the
    // instant transform over its `date` converts twice, because a date-only `YYYY-MM-DD`
    // deserialises to UTC midnight and `getLocalStoreDate` then renders that at the store's wall
    // clock — a day early for any store west of UTC, which silently drops that day's bonus. The
    // instant transform stays as the fallback: a Firestore dailyStats doc's `date` IS an instant.
    const businessDay = businessDayAsLocalDate((stat as Record<string, unknown>)?.businessDate as string | undefined)
    if (businessDay || statDate) {
      const localKey = format(businessDay ?? getLocalStoreDate(store || stat.store as string, statDate), 'd/M/y')
      statsByDay[localKey] = stat
    }
  }

  // Process bonuses for each employee-date combination
  for (const [key, employeeWhs] of Object.entries(whByEmployeeAndDate)) {
    const [employeeId, dateStr] = key.split('\u0000')
    const firstWh = employeeWhs[0] // Use first working hour record for this employee-date

    if (log) console.log(`Processing bonuses for employee ${employeeId} on ${dateStr} (${employeeWhs.length} working hour records)`)

    // day type
    if (firstWh.roleDetails?.roleBonusType === 'individualSalesPercent' && firstWh.roleDetails?.salaryType === 'day') {
      if (log) console.log(`Employee has daily percent bonus type`)
      const stat = statsByDay[dateStr]
      if (!stat) {
        if (log) console.log(`No stats found for date ${dateStr}`)
        continue
      }

      // check that wh have employee bonus amounts
      const employeeSalesAmount = (stat?.[employeeId + 'SalesAmount'] as number) || 0
      const employeeReturnsAmount = (stat?.[employeeId + 'ReturnsAmount'] as number) || 0
      const netSalesAmount = employeeSalesAmount - employeeReturnsAmount
      const hasMinSales = (!firstWh.roleDetails?.minIndividualSales || (firstWh.roleDetails?.minIndividualSales && firstWh.roleDetails?.minIndividualSales <= netSalesAmount))

      if (log) console.log(`Minimum sales requirement met: ${hasMinSales}, ${firstWh.roleDetails?.minIndividualSales} <= ${netSalesAmount}`)

      if (hasMinSales) {
        const storeData = storesDict?.[firstWh.store] as { dailyPercent?: number } | undefined
        const bonusPercent = firstWh.roleDetails?.individualSalesPercent ?? storeData?.dailyPercent ?? 0
        const calculatedBonus = netSalesAmount * bonusPercent / 100

        // Sum current bonuses from all working hour records for this employee-date
        const totalCurrentBonus = employeeWhs.reduce((sum, wh) => sum + (wh.employeeBonusAmount || 0), 0)

        if (log) console.log(`Employee sales: ${employeeSalesAmount}, Returns: ${employeeReturnsAmount}, Net: ${netSalesAmount}, Bonus percent: ${bonusPercent}%, Calculated: ${calculatedBonus}, Current total: ${totalCurrentBonus}`)

        // check if employee bonus is correct
        if (Math.abs(totalCurrentBonus - calculatedBonus) > 0.1) {
          if (log) console.log(`Updating employee bonus from ${totalCurrentBonus} to ${calculatedBonus}`)

          // Start batch logging
          const batchId = `batch_daily_bonus_${employeeId}_${dateStr}_${Date.now()}`
          dbWriteLogger.startBatch(batchId, `Update daily bonus for employee ${employeeId} on ${dateStr}`)

          // Use batch to make updates atomic and prevent loops
          const batch: FirestoreBatch = rootDb.batch()
          const bonusChanges: Array<{ id?: string; bonus: number }> = []

          // Clear bonuses from all records first, then assign full bonus to first record
          for (let i = 0; i < employeeWhs.length; i++) {
            const bonusToAssign = i === 0 ? calculatedBonus : 0
            const currentBonus = employeeWhs[i].employeeBonusAmount || 0

            if (Math.abs(currentBonus - bonusToAssign) > 0.1) {
              const updateFields = {employeeBonusAmount: bonusToAssign}
              const whRef = getFirestoreRef(employeeWhs[i], 'workingHours')
            batch.update(whRef, updateFields)
              bonusChanges.push({ id: employeeWhs[i].id, bonus: bonusToAssign })

              // Log the write
              dbWriteLogger.logWrite(
                'workingHours',
                whRef.id,
                updateFields,
                'update',
                {}
              )

              // Update local working hours array with new bonus amount
              employeeWhs[i].employeeBonusAmount = bonusToAssign
            }
          }

          // Update monthlyStats
          const firstWhDate = (firstWh.date as FirestoreTimestamp)?.toDate?.() || firstWh.date as Date
          const monthlyStatDocName = firstWh.monthlyStatID || getMonthlyStatDocID(firstWh.store, firstWhDate) || ''
          const monthlyStatFields = {
            [employeeId + 'MonthlyBonusAmount']: fieldValue.increment(calculatedBonus - totalCurrentBonus),
            monthlyBonusAmount: fieldValue.increment(calculatedBonus - totalCurrentBonus),
          }
          if (monthlyStatDocName) {
            batch.set(db.collection('monthlyStats').doc(monthlyStatDocName), monthlyStatFields, {merge: true})

            // Log monthlyStats write
            dbWriteLogger.logWrite(
              'monthlyStats',
              monthlyStatDocName,
              monthlyStatFields,
              'set',
              {merge: true}
            )
          }

          // Update user
          const userUpdateFields = {
            salaryCreditedAmount: fieldValue.increment(calculatedBonus - totalCurrentBonus),
          }
          batch.update(rootDb.collection('users').doc(employeeId), userUpdateFields)

          // Log user write
          dbWriteLogger.logWrite(
            'users',
            employeeId,
            userUpdateFields,
            'update',
            {}
          )

          // Commit batch atomically
          // Tolerant: a Supabase row whose Firestore twin is absent (backfills, the Tier-5 repairs)
          // would otherwise fail the commit and skip the Supabase mirror below — leaving the bonus in
          // NEITHER store. Only 'No document to update'/NOT_FOUND is swallowed; anything else throws.
          await commitBatchTolerant(batch, 'countEmployeeBonuses.daily')
          // ATL-1132 — see `dualWriteBonusAmounts`. After the Firestore commit, never before.
          await dualWriteBonusAmounts(bonusChanges)
          dbWriteLogger.endBatch()

          // Track the (day, store) pairs this batch actually wrote.
          for (const wh of employeeWhs) trackUpdatedDayStore(wh, store)
        } else {
          if (log) console.log(`Employee bonus is already correct: ${totalCurrentBonus}`)
        }
      } else {
        if (log) console.log(`Minimum sales requirement not met for daily employee. No bonus will be calculated.`)

        // Clear all bonuses for this employee-date
        const totalCurrentBonus = employeeWhs.reduce((sum, wh) => sum + (wh.employeeBonusAmount || 0), 0)

        if (totalCurrentBonus > 0) {
          // Start batch logging
          const batchId = `batch_clear_daily_bonus_${employeeId}_${dateStr}_${Date.now()}`
          dbWriteLogger.startBatch(batchId, `Clear daily bonus for employee ${employeeId} on ${dateStr}`)

          // Use batch to make updates atomic
          const batchClear: FirestoreBatch = rootDb.batch()
          const bonusChangesClear: Array<{ id?: string; bonus: number }> = []

          for (const wh of employeeWhs) {
            if ((wh.employeeBonusAmount || 0) > 0) {
              const updateFields = {employeeBonusAmount: 0}
              const whRefClear = getFirestoreRef(wh, 'workingHours')
              batchClear.update(whRefClear, updateFields)
              bonusChangesClear.push({ id: wh.id, bonus: 0 })

              // Log the write
              dbWriteLogger.logWrite(
                'workingHours',
                whRefClear.id,
                updateFields,
                'update',
                {}
              )

              // Update local working hours array with cleared bonus amount
              wh.employeeBonusAmount = 0
            }
          }

          // Update user
          const userUpdateFields = {
            salaryCreditedAmount: fieldValue.increment(-totalCurrentBonus),
          }
          batchClear.update(rootDb.collection('users').doc(employeeId), userUpdateFields)

          // Log user write
          dbWriteLogger.logWrite(
            'users',
            employeeId,
            userUpdateFields,
            'update',
            {}
          )

          // Commit batch atomically
          // Tolerant: a Supabase row whose Firestore twin is absent (backfills, the Tier-5 repairs)
          // would otherwise fail the commit and skip the Supabase mirror below — leaving the bonus in
          // NEITHER store. Only 'No document to update'/NOT_FOUND is swallowed; anything else throws.
          await commitBatchTolerant(batchClear, 'countEmployeeBonuses.dailyClear')
          // ATL-1132 — see `dualWriteBonusAmounts`. After the Firestore commit, never before.
          await dualWriteBonusAmounts(bonusChangesClear)
          dbWriteLogger.endBatch()

          // Track the (day, store) pairs this batch actually wrote.
          for (const wh of employeeWhs) trackUpdatedDayStore(wh, store)
        }
      }
    }

    // hour type
    if (firstWh.roleDetails?.roleBonusType === 'individualSalesPercent' && firstWh.roleDetails?.salaryType === 'hour') {
      if (log) console.log(`Employee has hourly individualSalesPercent bonus type`)
      const dayStat = statsByDay[dateStr]
      if (!dayStat) {
        if (log) console.log(`No stats found for date ${dateStr}`)
        continue
      }

      // Check minimum sales requirement, same as daily logic
      const employeeSalesAmount = (dayStat?.[employeeId + 'SalesAmount'] as number) || 0
      const employeeReturnsAmount = (dayStat?.[employeeId + 'ReturnsAmount'] as number) || 0
      const netSalesAmount = employeeSalesAmount - employeeReturnsAmount
      const storeData = storesDict?.[firstWh.store] as { dailyPercentMinValue?: number } | undefined
      const hasMinSales = (!storeData?.dailyPercentMinValue || (storeData?.dailyPercentMinValue && storeData?.dailyPercentMinValue <= netSalesAmount)) // old
       || (!firstWh.roleDetails?.minIndividualSales || (firstWh.roleDetails?.minIndividualSales && firstWh.roleDetails?.minIndividualSales <= netSalesAmount))

      if (log) console.log(`Minimum sales requirement met for hourly employee: ${hasMinSales}`)

      if (hasMinSales) {
        if (log) console.log(`Daily sales for employee: ${employeeSalesAmount}, Returns: ${employeeReturnsAmount}, Net: ${netSalesAmount}`)

        const bonusPercent = firstWh.roleDetails?.individualSalesPercent || 0
        const calculatedHourlyBonus = netSalesAmount * bonusPercent / 100

        // Sum current bonuses from all working hour records for this employee-date
        const totalCurrentBonus = employeeWhs.reduce((sum, wh) => sum + (wh.employeeBonusAmount || 0), 0)

        if (log) console.log(`Hourly bonus percent: ${bonusPercent}%, Calculated: ${calculatedHourlyBonus}, Current total: ${totalCurrentBonus}`)

        if (Math.abs(totalCurrentBonus - calculatedHourlyBonus) > 0.1) {
          if (log) console.log(`Updating hourly employee bonus from ${totalCurrentBonus} to ${calculatedHourlyBonus}`)

          // Start batch logging
          const batchId = `batch_hourly_bonus_${employeeId}_${dateStr}_${Date.now()}`
          dbWriteLogger.startBatch(batchId, `Update hourly bonus for employee ${employeeId} on ${dateStr}`)

          // Use batch to make updates atomic and prevent loops
          const batchHourly: FirestoreBatch = rootDb.batch()
          const bonusChangesHourly: Array<{ id?: string; bonus: number }> = []

          // Clear bonuses from all records first, then assign full bonus to first record
          for (let i = 0; i < employeeWhs.length; i++) {
            const bonusToAssign = i === 0 ? calculatedHourlyBonus : 0
            const currentBonus = employeeWhs[i].employeeBonusAmount || 0

            if (Math.abs(currentBonus - bonusToAssign) > 0.1) {
              const updateFields = {employeeBonusAmount: bonusToAssign}
              const whRefHourly = getFirestoreRef(employeeWhs[i], 'workingHours')
              batchHourly.update(whRefHourly, updateFields)
              bonusChangesHourly.push({ id: employeeWhs[i].id, bonus: bonusToAssign })

              // Log the write
              dbWriteLogger.logWrite(
                'workingHours',
                whRefHourly.id,
                updateFields,
                'update',
                {}
              )

              // Update local working hours array with new bonus amount
              employeeWhs[i].employeeBonusAmount = bonusToAssign
            }
          }

          // Update monthlyStats
          const firstWhDate = (firstWh.date as FirestoreTimestamp)?.toDate?.() || firstWh.date as Date
          const monthlyStatDocName = firstWh.monthlyStatID || getMonthlyStatDocID(firstWh.store, firstWhDate)
          if (monthlyStatDocName) {
            const monthlyStatFields = {
              [employeeId + 'MonthlyBonusAmount']: fieldValue.increment(calculatedHourlyBonus - totalCurrentBonus),
              monthlyBonusAmount: fieldValue.increment(calculatedHourlyBonus - totalCurrentBonus),
            }
            batchHourly.set(db.collection('monthlyStats').doc(monthlyStatDocName), monthlyStatFields, {merge: true})

            // Log monthlyStats write
            dbWriteLogger.logWrite(
              'monthlyStats',
              monthlyStatDocName,
              monthlyStatFields,
              'set',
              {merge: true}
            )
          }

          // Update user
          const userUpdateFields = {
            salaryCreditedAmount: fieldValue.increment(calculatedHourlyBonus - totalCurrentBonus),
          }
          batchHourly.update(rootDb.collection('users').doc(employeeId), userUpdateFields)

          // Log user write
          dbWriteLogger.logWrite(
            'users',
            employeeId,
            userUpdateFields,
            'update',
            {}
          )

          // Commit batch atomically
          // Tolerant: a Supabase row whose Firestore twin is absent (backfills, the Tier-5 repairs)
          // would otherwise fail the commit and skip the Supabase mirror below — leaving the bonus in
          // NEITHER store. Only 'No document to update'/NOT_FOUND is swallowed; anything else throws.
          await commitBatchTolerant(batchHourly, 'countEmployeeBonuses.hourly')
          // ATL-1132 — see `dualWriteBonusAmounts`. After the Firestore commit, never before.
          await dualWriteBonusAmounts(bonusChangesHourly)
          dbWriteLogger.endBatch()

          // Track the (day, store) pairs this batch actually wrote.
          for (const wh of employeeWhs) trackUpdatedDayStore(wh, store)
        } else {
          if (log) console.log(`Hourly employee bonus is already correct: ${totalCurrentBonus}`)
        }
      } else {
        if (log) console.log(`Minimum sales requirement not met for hourly employee. No bonus will be calculated.`)

        // Clear all bonuses for this employee-date
        const totalCurrentBonus = employeeWhs.reduce((sum, wh) => sum + (wh.employeeBonusAmount || 0), 0)

        if (totalCurrentBonus > 0) {
          // Start batch logging
          const batchId = `batch_clear_hourly_bonus_${employeeId}_${dateStr}_${Date.now()}`
          dbWriteLogger.startBatch(batchId, `Clear hourly bonus for employee ${employeeId} on ${dateStr}`)

          // Use batch to make updates atomic
          const batchClearHourly: FirestoreBatch = rootDb.batch()
          const bonusChangesClearHourly: Array<{ id?: string; bonus: number }> = []

          for (const wh of employeeWhs) {
            if ((wh.employeeBonusAmount || 0) > 0) {
              const updateFields = {employeeBonusAmount: 0}
              const whRefClearHourly = getFirestoreRef(wh, 'workingHours')
              batchClearHourly.update(whRefClearHourly, updateFields)
              bonusChangesClearHourly.push({ id: wh.id, bonus: 0 })

              // Log the write
              dbWriteLogger.logWrite(
                'workingHours',
                whRefClearHourly.id,
                updateFields,
                'update',
                {}
              )

              // Update local working hours array with cleared bonus amount
              wh.employeeBonusAmount = 0
            }
          }

          // Commit batch atomically
          // Tolerant: a Supabase row whose Firestore twin is absent (backfills, the Tier-5 repairs)
          // would otherwise fail the commit and skip the Supabase mirror below — leaving the bonus in
          // NEITHER store. Only 'No document to update'/NOT_FOUND is swallowed; anything else throws.
          await commitBatchTolerant(batchClearHourly, 'countEmployeeBonuses.hourlyClear')
          // ATL-1132 — see `dualWriteBonusAmounts`. After the Firestore commit, never before.
          await dualWriteBonusAmounts(bonusChangesClearHourly)
          dbWriteLogger.endBatch()

          // Track the (day, store) pairs this batch actually wrote.
          for (const wh of employeeWhs) trackUpdatedDayStore(wh, store)
        }
      }
    }
  }

  // Handle monthly bonuses - group by employee and month to avoid duplication within same month
  const monthlyWhs = identifiedWorkingHours.filter(wh => wh.roleDetails?.roleBonusType === 'individualSalesPercent' && wh.roleDetails?.salaryType === 'month')

  // Group monthly working hours by employee and month
  const monthlyWhsByEmployeeAndMonth: Record<string, WorkingHoursInfo[]> = {}
  for (const wh of monthlyWhs) {
    const employeeId = wh.employeeID
    const whDate = (wh.date as FirestoreTimestamp)?.toDate?.() || wh.date as Date
    const monthKey = format(whDate, 'M-y') // Group by month-year
    const key = `${employeeId}\u0000${monthKey}`

    if (!monthlyWhsByEmployeeAndMonth[key]) {
      monthlyWhsByEmployeeAndMonth[key] = []
    }
    monthlyWhsByEmployeeAndMonth[key].push(wh)
  }

  if (log) console.log(`Grouped ${monthlyWhs.length} monthly working hours into ${Object.keys(monthlyWhsByEmployeeAndMonth).length} employee-month combinations`)

  for (const [key, employeeMonthlyWhs] of Object.entries(monthlyWhsByEmployeeAndMonth)) {
    const [employeeId, monthKey] = key.split('\u0000')
    const firstWh = employeeMonthlyWhs[0] // Use first working hour record for this employee-month

    if (log) console.log(`Processing monthly bonuses for employee ${employeeId} in month ${monthKey} (${employeeMonthlyWhs.length} monthly working hour records)`)

    // Calculate sales for this month by finding the period that covers the whole month
    // Use the earliest startDate and latest endDate from all records in this month
    const startDate = employeeMonthlyWhs.reduce((earliest: Date | null, wh) => {
      const whStartDate = (wh.startDate as FirestoreTimestamp)?.toDate ? (wh.startDate as FirestoreTimestamp).toDate() : wh.startDate as Date
      return !earliest || whStartDate < earliest ? whStartDate : earliest
    }, null as Date | null)
    const endDate = employeeMonthlyWhs.reduce((latest: Date | null, wh) => {
      const whEndDate = (wh.endDate as FirestoreTimestamp)?.toDate ? (wh.endDate as FirestoreTimestamp).toDate() : wh.endDate as Date
      return !latest || whEndDate > latest ? whEndDate : latest
    }, null as Date | null)
    // startDate and endDate will always have values since employeeMonthlyWhs is non-empty
    const localDates = dateRangeWithTimezoneAndAutoclose({startDate: startDate!, endDate: endDate!}, [firstWh.store])
    const monthSalesTotal = Object.values(statsData || {}).filter(stat => {
      const statDate = (stat.date as FirestoreTimestamp)?.toDate ? (stat.date as FirestoreTimestamp).toDate() : stat.date as Date
      return statDate >= localDates.startDate! && statDate <= localDates.endDate!
    }).reduce((acc, curr) => acc + ((curr[employeeId + 'SalesAmount'] as number) || 0), 0)

    const monthReturnsTotal = Object.values(statsData || {}).filter(stat => {
      const statDate = (stat.date as FirestoreTimestamp)?.toDate ? (stat.date as FirestoreTimestamp).toDate() : stat.date as Date
      return statDate >= localDates.startDate! && statDate <= localDates.endDate!
    }).reduce((acc, curr) => acc + ((curr[employeeId + 'ReturnsAmount'] as number) || 0), 0)

    const monthNetSales = monthSalesTotal - monthReturnsTotal

    if (log) console.log(`Monthly sales total for employee in ${monthKey}: ${monthSalesTotal}, Returns: ${monthReturnsTotal}, Net: ${monthNetSales}`)

    const bonusPercent = firstWh.roleDetails?.individualSalesPercent || 0
    const calculatedMonthBonus = monthNetSales * bonusPercent / 100

    // Sum current bonuses from all monthly working hour records for this employee-month
    const totalCurrentBonus = employeeMonthlyWhs.reduce((sum, wh) => sum + (wh.employeeBonusAmount || 0), 0)

    if (log) console.log(`Monthly bonus percent: ${bonusPercent}%, Calculated: ${calculatedMonthBonus}, Current total: ${totalCurrentBonus}`)

    if (Math.abs(totalCurrentBonus - calculatedMonthBonus) > 0.1) {
      if (log) console.log(`Updating monthly employee bonus from ${totalCurrentBonus} to ${calculatedMonthBonus}`)

      // Start batch logging
      const batchId = `batch_monthly_bonus_${employeeId}_${monthKey}_${Date.now()}`
      dbWriteLogger.startBatch(batchId, `Update monthly bonus for employee ${employeeId} in ${monthKey}`)

      // Use batch to make updates atomic and prevent loops
      const batchMonthlyBonus: FirestoreBatch = rootDb.batch()
      const bonusChangesMonthly: Array<{ id?: string; bonus: number }> = []
      // ATL-1153 — the records whose stored bonus this batch actually changes. Each one's own day
      // needs reconciling, and they span the month.
      const changedMonthlyWhs: WorkingHoursInfo[] = []

      // Clear bonuses from all records first, then assign full bonus to first record
      for (let i = 0; i < employeeMonthlyWhs.length; i++) {
        const bonusToAssign = i === 0 ? calculatedMonthBonus : 0
        const currentBonus = employeeMonthlyWhs[i].employeeBonusAmount || 0

        if (Math.abs(currentBonus - bonusToAssign) > 0.1) {
          const updateFields = {employeeBonusAmount: bonusToAssign}
          const whRefMonthly = getFirestoreRef(employeeMonthlyWhs[i], 'workingHours')
          batchMonthlyBonus.update(whRefMonthly, updateFields)
          bonusChangesMonthly.push({ id: employeeMonthlyWhs[i].id, bonus: bonusToAssign })

          // Log the write
          dbWriteLogger.logWrite(
            'workingHours',
            whRefMonthly.id,
            updateFields,
            'update',
            {}
          )

          // Update local working hours array with new bonus amount
          employeeMonthlyWhs[i].employeeBonusAmount = bonusToAssign
          changedMonthlyWhs.push(employeeMonthlyWhs[i])
        }
      }

      // Update monthlyStats
      const firstWhDate = (firstWh.date as FirestoreTimestamp)?.toDate?.() || firstWh.date as Date
      const monthlyStatDocName = firstWh.monthlyStatID || getMonthlyStatDocID(firstWh.store, firstWhDate)
      if (monthlyStatDocName) {
        const monthlyStatFields = {
          [employeeId + 'MonthlyBonusAmount']: fieldValue.increment(calculatedMonthBonus - totalCurrentBonus),
          monthlyBonusAmount: fieldValue.increment(calculatedMonthBonus - totalCurrentBonus),
          date: firstWh.date,
          store: firstWh.store,
          organizationId: jotaiStore.get(organizationIdAtom)
        }
        batchMonthlyBonus.set(db.collection('monthlyStats').doc(monthlyStatDocName), monthlyStatFields, {merge: true})

        // Log monthlyStats write
        dbWriteLogger.logWrite(
          'monthlyStats',
          monthlyStatDocName,
          monthlyStatFields,
          'set',
          {merge: true}
        )
      }

      // Update user
      const userUpdateFields = {
        salaryCreditedAmount: fieldValue.increment(calculatedMonthBonus - totalCurrentBonus),
      }
      batchMonthlyBonus.update(rootDb.collection('users').doc(employeeId), userUpdateFields)

      // Log user write
      dbWriteLogger.logWrite(
        'users',
        employeeId,
        userUpdateFields,
        'update',
        {}
      )

      // Commit batch atomically
      // Tolerant: a Supabase row whose Firestore twin is absent (backfills, the Tier-5 repairs)
      // would otherwise fail the commit and skip the Supabase mirror below — leaving the bonus in
      // NEITHER store. Only 'No document to update'/NOT_FOUND is swallowed; anything else throws.
      await commitBatchTolerant(batchMonthlyBonus, 'countEmployeeBonuses.monthly')
      // ATL-1132 — see `dualWriteBonusAmounts`. After the Firestore commit, never before.
      await dualWriteBonusAmounts(bonusChangesMonthly)
      dbWriteLogger.endBatch()

      // Track every (day, store) whose bonus this batch changed — NOT just `firstWh`'s. The loop
      // above clears the bonus on records spanning the whole month, and each of those days has its
      // own `dailyStats` document.
      for (const changedWh of changedMonthlyWhs) trackUpdatedDayStore(changedWh, store)
    } else {
      if (log) console.log(`Monthly employee bonus is already correct: ${totalCurrentBonus}`)
    }
  }

  if (log) console.log('Finished processing all working hours')

  // Reconcile the daily stats of every (business day, store) whose bonuses changed.
  //
  // The instant is passed RAW and the store comes from the pair, never from the function's single
  // recovered `store`. `validateDailyStats` localizes the date itself (`getLocalStoreDate(store,
  // date)`), as every other caller assumes — `SalesStats.js`, `MiscScreen.js` and
  // `dailyStatsValidationSimple.js` all hand it an unlocalized `Date` — and localizing here first
  // applied the shift twice, moving the whole reconciliation to a neighbouring day on any device
  // outside the store's zone.
  //
  // No working-hours set is handed over. Whatever this function is working from is only ever one
  // employee's records when it was given a `userId` or a caller-supplied `whs`, which is exactly
  // the `SalaryScreen` call; `dailyStats.salaryAmount` / `employeeBonusAmount` are STORE-day totals
  // over every employee, rewritten as ABSOLUTE values, so a per-employee subset rewrote the store's
  // day to one person's figures. `validateDailyStats` reads the whole store day itself.
  //
  // `automaticFixes: false` — deliberate, and NOT a claim that an operator chose the day.
  //
  // I changed this to `true` in review round 2 on the reasoning that nothing here is operator-
  // triggered, which is true but is not what the flag gates. All it does is arm the
  // `userInDifferentTimeZone` suppression, and that atom (`AtomsProvider.ts`) compares the DEVICE's
  // current hour against the USER'S OWN store's — it says nothing about the record's day or about
  // the store being reconciled. This branch's inputs are entirely store-framed: the day comes from
  // the RECORD's timestamp, `localDate` and the comparison window are resolved in the target
  // store's zone, and the query compares instants. So the suppression's condition is uncorrelated
  // with any hazard here — a guard whose predicate names something other than the capability that
  // makes harm reachable blocks the wrong population.
  //
  // What arming it actually cost: `countEmployeeBonuses` is the ONLY writer of
  // `dailyStats.employeeBonusAmount`, and the two other callers passing `{workingHours: true}`
  // (`SalesStats.js`, `MiscScreen.js`) also pass `{sales, spendings}`, which returns
  // `skip('supabase-read-mode')` for every cut-over org. On a device whose hour differs from its
  // store's, these four fields would have had NO repair path at all.
  if (updatedDayStores.size > 0) {
    if (log) console.log(`Validating daily stats for ${updatedDayStores.size} updated (day, store) pair(s)`)

    for (const { store: whStore, date: whDate } of updatedDayStores.values()) {
      if (log) console.log(`Validating daily stats for store ${whStore} on ${whDate.toISOString()}`)
      await validateDailyStats(whDate, whStore, true, {workingHours: true}, null, log, false, 'countEmployeeBonuses')
    }

    if (log) console.log('Finished validating daily stats for all updated (day, store) pairs')
  }

  // End database write logging
  dbWriteLogger.endFunction(logId)
}

// ============================================================================
// Automatic Working Hours Creation
// ============================================================================

/**
 * Creates automatic working hours for employees with individualSalesPercent bonus type and day salary type
 *
 * @param store - Store ID.
 * @param range - Date range object.
 * @param whs - Array of working hour records.
 * @param stats - Stats object.
 * @param userId - User ID.
 * @param log - Whether to log the process.
 */
export const createAutomaticWorkingHours = async (
  store: string,
  range: DateRange,
  whs: WorkingHoursInfo[],
  stats: Stats,
  userId: string,
  log = false
): Promise<void> => {
  if (log) console.log('Starting createAutomaticWorkingHours for store:', store)
  if (log) console.log('Date range:', format(range.startDate, 'd/M/y'), 'to', format(range.endDate, 'd/M/y'))

  // Get current date to last day of range
  let currentDate = new Date(range.startDate)
  const endDate = new Date(range.endDate)

  // Check if the range is in current calendar month
  const now = new Date()
  const isCurrentMonth = currentDate.getMonth() === now.getMonth() &&
                        currentDate.getFullYear() === now.getFullYear()
  const isPreviousMonth = (currentDate.getMonth() === now.getMonth() - 1 &&
                          currentDate.getFullYear() === now.getFullYear()) ||
                         (currentDate.getMonth() === 11 &&
                          now.getMonth() === 0 &&
                          currentDate.getFullYear() === now.getFullYear() - 1)

  if (!isCurrentMonth && !isPreviousMonth) {
    if (log) console.log('Skipping automatic working hour creation - not in current or previous month')
    return
  }
  if (log) console.log('Processing dates in', isCurrentMonth ? 'current' : 'previous', 'month')

  const jotaiStore = getDefaultStore()
  const usersDict = jotaiStore.get(usersDictAtom) as Record<string, { uid?: string; name?: string; role?: string }>
  const user = usersDict[userId]
  const rolesDict = jotaiStore.get(rolesDictAtom) as Record<string, RoleDetails>

  while (currentDate <= endDate) {
    const localDate = getLocalStoreDate(store, currentDate)
    const dateStr = format(localDate, 'd/M/y E')
    if (log) console.log('\nProcessing date:', dateStr)

    // Check stats exists for this date
    if (stats[dateStr]) {
      if (log) console.log('Found stats for date:', dateStr)
      const salesKey = userId + 'SalesAmount'

      if (stats[dateStr][salesKey]) {
        // Check if this user already has working hours for this date
        const existingWh = whs.find(wh =>
          wh.employeeID === user?.uid &&
          format(getLocalStoreDate(store, ((wh.date as FirestoreTimestamp)?.toDate?.() || wh.date as Date)), 'd/M/y') === format(localDate, 'd/M/y')
        )

        // Only create working hours if the employee made sales on this date and has individualSalesPercent bonus type
        const employeeSalesAmount = stats[dateStr][salesKey] as number
        if (!existingWh &&
            employeeSalesAmount &&
            employeeSalesAmount > 0 &&
            rolesDict?.[user?.role || '']?.roleBonusType === 'individualSalesPercent' &&
            rolesDict?.[user?.role || '']?.salaryType === 'day') {
          if (log) console.log('Creating working hours for user:', user?.name, 'with sales amount:', employeeSalesAmount)
          // Create a working hour record
          const newWh: WorkingHoursInfo = {
            date: localDate,
            employeeID: user?.uid,
            role: user?.role,
            roleDetails: rolesDict?.[user?.role || ''],
            salaryType: 'day',
            amount: rolesDict?.[user?.role || '']?.salary || 0,
            dailyStatID: stats[dateStr].id,
            store: store,
          }

          // Add to database
          const docRef = await saveWorkingHours(null, newWh)
          // Add to working hours array for processing bonuses
          newWh.id = docRef.id
          newWh.ref = docRef
          whs.push(newWh)
          if (log) console.log('Successfully created working hours for user:', user?.name)
        }
      }

    } else {
      if (log) console.log('No stats found for date:', dateStr)
    }

    // Move to next day
    currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)
  }
  if (log) console.log('\nFinished processing all dates')
}
